/**
 * Accessibility-filter selection keys (DEV-600).
 *
 * A selection is a list of string keys: `"57:428"` = tag 428 in section 57;
 * `"428"` = tag 428 with no section (a legacy tag-only input, or one of the
 * step-free alternatives, which are never sectioned). Canonical form: a tag key
 * and pair keys for the same tag never coexist. Pure, DOM-free — safe for native.
 */

export type SelectionKey = string;

/** The "Would any of these also work?" tags, nested under a ticked step-free access box (428). */
// Each merged id stays beside its survivor (964 → 591, 753 → 602): an environment the merge has not reached lists the old ones.
export const STEP_FREE_ALTERNATIVE_IDS: number[] = [964, 591, 844, 553, 554, 753, 602, 819];

/** A section id → the ids of the filter tags it offers. */
export type TagSections = Map<number, number[]>;

export interface SelectionPlacement {
  tag_id: number;
  heading_id: number;
}

interface SectionLike {
  id: number;
  accessibility_tags?: { id: number }[];
}

interface SavedRowLike {
  tag_id: number;
  heading_id?: number | null;
}

export function pairKey(headingId: number, tagId: number): SelectionKey {
  return `${headingId}:${tagId}`;
}

export function tagKey(tagId: number): SelectionKey {
  return String(tagId);
}

export function parseKey(key: SelectionKey): { tagId: number; headingId: number | null } | null {
  const parts = key.split(":");
  if (parts.length === 1) {
    const tagId = Number(parts[0]);
    return Number.isInteger(tagId) && tagId > 0 ? { tagId, headingId: null } : null;
  }
  if (parts.length === 2) {
    const headingId = Number(parts[0]);
    const tagId = Number(parts[1]);
    return Number.isInteger(headingId) && headingId > 0 && Number.isInteger(tagId) && tagId > 0
      ? { tagId, headingId }
      : null;
  }
  return null;
}

function unique(keys: SelectionKey[]): SelectionKey[] {
  return Array.from(new Set(keys));
}

function isPairKey(key: SelectionKey): boolean {
  const parsed = parseKey(key);
  return parsed !== null && parsed.headingId !== null;
}

/** Unique tag ids, in order of first appearance. */
export function tagIdsOf(keys: SelectionKey[]): number[] {
  const ids: number[] = [];
  for (const key of keys) {
    const parsed = parseKey(key);
    if (parsed && !ids.includes(parsed.tagId)) ids.push(parsed.tagId);
  }
  return ids;
}

export function isTagSelected(keys: SelectionKey[], tagId: number): boolean {
  return keys.some((key) => parseKey(key)?.tagId === tagId);
}

/** A checkbox instance is ticked by its own pair, or by a section-less key for the tag. */
export function isInstanceSelected(keys: SelectionKey[], headingId: number, tagId: number): boolean {
  return keys.includes(pairKey(headingId, tagId)) || keys.includes(tagKey(tagId));
}

export function buildTagSections(sections: SectionLike[]): TagSections {
  const map: TagSections = new Map();
  for (const section of sections) {
    for (const tag of section.accessibility_tags ?? []) {
      const list = map.get(tag.id) ?? [];
      if (!list.includes(section.id)) list.push(section.id);
      map.set(tag.id, list);
    }
  }
  return map;
}

/**
 * `tagSections` plus each family member (the step-free alternatives) mapped to
 * the sections where `parentId` is currently ticked, so `toggleInstance` treats
 * an alternative as "this alternative under this ticked parent instance".
 */
export function withFamilySections(
  tagSections: TagSections,
  keys: SelectionKey[],
  parentId: number,
  familyIds: number[],
): TagSections {
  const ticked = (tagSections.get(parentId) ?? []).filter((sectionId) =>
    isInstanceSelected(keys, sectionId, parentId),
  );
  const map: TagSections = new Map(tagSections);
  for (const id of familyIds) map.set(id, [...ticked]);
  return map;
}

/** Every section each tag appears in across the given maps, first map's order first. */
export function mergeTagSections(...maps: TagSections[]): TagSections {
  const merged: TagSections = new Map();
  for (const map of maps) {
    map.forEach((sections, tagId) => {
      const list = [...(merged.get(tagId) ?? [])];
      for (const sectionId of sections) {
        if (!list.includes(sectionId)) list.push(sectionId);
      }
      merged.set(tagId, list);
    });
  }
  return merged;
}

/**
 * Splits an `accessibilities` / `accessibility_sections` pair into keys. A tag
 * id gets a section-less key only when no pair covers it; a pair whose tag is
 * absent from `accessibilities` is dropped, mirroring the backend.
 */
export function decodeSelection(accessibilities?: string | null, sections?: string | null): SelectionKey[] {
  const tagIds = (accessibilities ?? "")
    .split("|")
    .map((part) => parseKey(part.trim()))
    .filter((parsed): parsed is { tagId: number; headingId: null } => parsed !== null && parsed.headingId === null)
    .map((parsed) => parsed.tagId);
  const pairs = (sections ?? "")
    .split("|")
    .map((part) => parseKey(part.trim()))
    .filter((parsed): parsed is { tagId: number; headingId: number } => parsed !== null && parsed.headingId !== null)
    .filter((parsed) => tagIds.includes(parsed.tagId));

  const keys = pairs.map((p) => pairKey(p.headingId, p.tagId));
  const paired = new Set(pairs.map((p) => p.tagId));
  for (const tagId of tagIds) {
    if (!paired.has(tagId)) keys.push(tagKey(tagId));
  }
  return unique(keys);
}

/** `accessibilities` = every tag id; `accessibility_sections` = the pair keys ("" when none). */
export function encodeSelection(keys: SelectionKey[]): { accessibilities: string; accessibility_sections: string } {
  return {
    accessibilities: tagIdsOf(keys).join("|"),
    accessibility_sections: unique(keys.filter((key) => isPairKey(key))).join("|"),
  };
}

/**
 * A tag key and pair keys for the same tag never coexist: the tag key is
 * materialised into a pair for every section of the tag first.
 */
export function canonicalise(keys: SelectionKey[], tagSections: TagSections): SelectionKey[] {
  const pairedTags = new Set<number>();
  for (const key of keys) {
    const parsed = parseKey(key);
    if (parsed && parsed.headingId !== null) pairedTags.add(parsed.tagId);
  }
  const out: SelectionKey[] = [];
  for (const key of keys) {
    const parsed = parseKey(key);
    if (!parsed) continue;
    if (parsed.headingId === null && pairedTags.has(parsed.tagId)) {
      for (const sectionId of tagSections.get(parsed.tagId) ?? []) out.push(pairKey(sectionId, parsed.tagId));
    } else {
      out.push(key);
    }
  }
  return unique(out);
}

/** Tick / untick one checkbox: a tick adds this section only; what an untick leaves of a section-less key spreads over `tagSections`. */
export function toggleInstance(
  keys: SelectionKey[],
  headingId: number,
  tagId: number,
  tagSections: TagSections,
  allSections: TagSections = tagSections,
): SelectionKey[] {
  const sections = tagSections.get(tagId) ?? [];
  if (sections.length === 0) {
    const key = tagKey(tagId);
    return keys.includes(key) ? keys.filter((k) => k !== key) : unique([...keys, key]);
  }

  const pair = pairKey(headingId, tagId);
  if (keys.includes(pair)) {
    return canonicalise(keys.filter((k) => k !== pair), allSections);
  }
  // A basic untick of a section-less key keeps the basic sections only: a pair left in an off-screen complex sub-heading keeps the filter on unseen.
  if (keys.includes(tagKey(tagId))) {
    return untickCoveredInstance(keys, headingId, tagId, tagSections, allSections);
  }
  return canonicalise([...keys, pair], allSections);
}

/** Tick / untick one checkbox in its own section only (complex sections: no venue has a feature in every sub-heading). */
export function toggleSectionOnly(
  keys: SelectionKey[],
  headingId: number,
  tagId: number,
  allSections: TagSections,
): SelectionKey[] {
  const pair = pairKey(headingId, tagId);
  if (keys.includes(pair)) {
    return canonicalise(keys.filter((k) => k !== pair), allSections);
  }
  // A complex untick keeps every other box: a tag only sub-headings list has no basic sections to fall back to.
  if (keys.includes(tagKey(tagId))) {
    return untickCoveredInstance(keys, headingId, tagId, allSections, allSections);
  }
  return canonicalise([...keys, pair], allSections);
}

/**
 * A box ticked through a section-less key: the key becomes pairs on `spread`, minus this box.
 * @param spread Where the rest of the key stays ticked.
 */
function untickCoveredInstance(
  keys: SelectionKey[],
  headingId: number,
  tagId: number,
  spread: TagSections,
  allSections: TagSections,
): SelectionKey[] {
  const others = (spread.get(tagId) ?? []).filter((s) => s !== headingId).map((s) => pairKey(s, tagId));
  return canonicalise([...keys.filter((k) => k !== tagKey(tagId)), ...others], allSections);
}

export function unionSelections(keys: SelectionKey[], other: SelectionKey[], tagSections: TagSections): SelectionKey[] {
  return canonicalise(unique([...keys, ...other]), tagSections);
}

/**
 * Removes `other` from `keys`, representation-aware: a section-less key in
 * `other` removes every key of that tag (a legacy saved tick covered all
 * sections); a pair removes itself and, if the tag was ticked via a section-less
 * key, materialises the other sections.
 */
export function subtractSelections(keys: SelectionKey[], other: SelectionKey[], tagSections: TagSections): SelectionKey[] {
  let out = [...keys];
  for (const key of other) {
    const parsed = parseKey(key);
    if (!parsed) continue;
    if (parsed.headingId === null) {
      out = removeTag(out, parsed.tagId);
      continue;
    }
    if (out.includes(tagKey(parsed.tagId))) {
      const others = (tagSections.get(parsed.tagId) ?? [])
        .filter((s) => s !== parsed.headingId)
        .map((s) => pairKey(s, parsed.tagId));
      out = [...out.filter((k) => k !== tagKey(parsed.tagId)), ...others];
    }
    out = out.filter((k) => k !== key);
  }
  return canonicalise(unique(out), tagSections);
}

/**
 * Reconciles keys with a plain tag-id list written by a caller that knows
 * nothing about sections: retained tags keep their keys, removed tags lose
 * them, new tags get a section-less key.
 */
export function reconcile(selections: SelectionKey[], tagIds: number[]): SelectionKey[] {
  const out = selections.filter((key) => {
    const parsed = parseKey(key);
    return parsed !== null && tagIds.includes(parsed.tagId);
  });
  for (const tagId of tagIds) {
    if (!isTagSelected(out, tagId)) out.push(tagKey(tagId));
  }
  return unique(out);
}

export function removeTag(keys: SelectionKey[], tagId: number): SelectionKey[] {
  return keys.filter((key) => parseKey(key)?.tagId !== tagId);
}

/** Removes a tag's section-less key and pairs, keeping its pairs in `keepSectionIds`. */
export function removeTagExcept(keys: SelectionKey[], tagId: number, keepSectionIds: number[]): SelectionKey[] {
  return keys.filter((key) => {
    const parsed = parseKey(key);
    if (!parsed || parsed.tagId !== tagId) return true;
    return parsed.headingId !== null && keepSectionIds.includes(parsed.headingId);
  });
}

/** Whether the tag is ticked by a section-less key or by a pair outside `excludeSectionIds`. */
export function isTagSelectedOutside(keys: SelectionKey[], tagId: number, excludeSectionIds: number[]): boolean {
  return keys.some((key) => {
    const parsed = parseKey(key);
    return parsed !== null && parsed.tagId === tagId
      && (parsed.headingId === null || !excludeSectionIds.includes(parsed.headingId));
  });
}

/** Saved-preference rows → keys; a row without a section is a section-less key. */
export function keysFromSavedRows(rows: SavedRowLike[]): SelectionKey[] {
  return unique(
    rows.map((row) => (row.heading_id ? pairKey(row.heading_id, row.tag_id) : tagKey(row.tag_id))),
  );
}

/** "What Matters To You" group tags → keys; a plain key drops the tag's pairs, as it already covers every heading. */
export function matterKeys(children: { id: number; heading_id?: number | null }[]): SelectionKey[] {
  const keys = keysFromSavedRows(children.map((child) => ({ tag_id: child.id, heading_id: child.heading_id })));
  const plain = new Set(keys.filter((key) => !isPairKey(key)).map((key) => parseKey(key)?.tagId));
  return keys.filter((key) => !isPairKey(key) || !plain.has(parseKey(key)?.tagId));
}

/** Pair keys → placements for the save endpoint (section-less keys are omitted). */
export function placementsOf(keys: SelectionKey[]): SelectionPlacement[] {
  const out: SelectionPlacement[] = [];
  for (const key of unique(keys)) {
    const parsed = parseKey(key);
    if (parsed?.headingId) out.push({ tag_id: parsed.tagId, heading_id: parsed.headingId });
  }
  return out;
}
